import React from 'react';
import { Autocomplete, Box, Checkbox, FormControlLabel, FormHelperText, MenuItem, TextField } from '@mui/material';
import Group from 'components/common/Group';
import { renderField } from 'components/utils';
import { fetchIndeedLocationSuggestions } from './fetchIndeedLocationSuggestions';
import { useExtensionDetection } from 'lib/useExtensionDetection';
import ExtensionLocationAutocomplete from './ExtensionLocationAutocomplete';
import { IndeedFields, IndeedJobType, IndeedShowPayBy } from '../types';
import {
  getIndeedBenefitPickerOptions,
  indeedExpectedHoursShowByOptions,
  indeedHiringTimelineOptions,
  indeedJobLocationFieldLabel,
  indeedJobLocationTypeOptions,
  indeedJobLocationTypeShowsRelocate,
  indeedJobLocationTypeShowsRemoteResidenceQuestion,
  indeedJobTypeIncludesPartTime,
  indeedJobTypeOptions,
  indeedPayRateOptions,
  indeedPayUsesAmount,
  indeedPayUsesMinimumAndMaximum,
  indeedShowPayByOptions,
  indeedShowsJobLocation,
} from './indeedConstants';

type Props = {
  value: IndeedFields;
  onChange: (value: IndeedFields) => void;
  infoAlert?: React.ReactNode;
  wrapInGroup?: boolean;
  hideGroupTitle?: boolean;
};

const flexOptions = {
  label: 3,
  value: 9,
};

const parsePayAmount = (raw: string): string | undefined => {
  const parsed = Number.parseFloat(raw.replace(/,/g, ''));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed.toLocaleString('en-US') : undefined;
};

const payFieldDisplay = (value?: string | number): string => {
  if (value == null || value === '') {
    return '';
  }

  if (typeof value === 'number') {
    return value.toLocaleString('en-US');
  }

  return value;
};

const IndeedFieldsForm = ({ value, onChange, infoAlert, wrapInGroup = true, hideGroupTitle = false }: Props) => {
  const { installed: extensionInstalled } = useExtensionDetection();
  const showJobLocation = indeedShowsJobLocation(value.jobLocationType, value.requireRemoteResidence);
  const showRemoteResidenceQuestion = indeedJobLocationTypeShowsRemoteResidenceQuestion(value.jobLocationType);
  const showWillingToRelocate = indeedJobLocationTypeShowsRelocate(value.jobLocationType, value.requireRemoteResidence);
  const showExpectedHours = indeedJobTypeIncludesPartTime(value.jobTypes);
  const jobLocationLabel = indeedJobLocationFieldLabel(value.jobLocationType, value.requireRemoteResidence);
  const expectedHoursShowBy = value.expectedHoursShowBy || 'Fixed hours';
  const showPayBy = value.showPayBy || 'Range';
  const selectedBenefits = value.benefits || [];

  const updateField = <K extends keyof IndeedFields>(key: K, next: IndeedFields[K]) => {
    onChange({ ...value, [key]: next });
  };

  const updateLocationType = (nextType: IndeedFields['jobLocationType']) => {
    const nextValue: IndeedFields = { ...value, jobLocationType: nextType };

    if (!indeedJobLocationTypeShowsRemoteResidenceQuestion(nextType)) {
      nextValue.requireRemoteResidence = undefined;
    }

    if (!indeedJobLocationTypeShowsRelocate(nextType, nextValue.requireRemoteResidence)) {
      nextValue.includeWillingToRelocate = false;
    }

    if (!indeedShowsJobLocation(nextType, nextValue.requireRemoteResidence)) {
      nextValue.jobLocation = undefined;
    }

    onChange(nextValue);
  };

  const updateRequireRemoteResidence = (nextValue: boolean | undefined) => {
    const nextFields: IndeedFields = { ...value, requireRemoteResidence: nextValue };

    if (!indeedShowsJobLocation(value.jobLocationType, nextValue)) {
      nextFields.jobLocation = undefined;
    }

    if (!indeedJobLocationTypeShowsRelocate(value.jobLocationType, nextValue)) {
      nextFields.includeWillingToRelocate = false;
    }

    onChange(nextFields);
  };

  const toggleJobType = (jobType: IndeedJobType, checked: boolean) => {
    const current = value.jobTypes || [];
    const nextTypes = checked ? Array.from(new Set([...current, jobType])) : current.filter(type => type !== jobType);
    const nextFields: IndeedFields = {
      ...value,
      jobTypes: nextTypes.length > 0 ? nextTypes : undefined,
    };

    if (!indeedJobTypeIncludesPartTime(nextTypes)) {
      nextFields.expectedHoursShowBy = undefined;
      nextFields.expectedHoursAt = undefined;
      nextFields.expectedHoursFrom = undefined;
      nextFields.expectedHoursTo = undefined;
      nextFields.expectedHoursMaximum = undefined;
      nextFields.expectedHoursMinimum = undefined;
    }

    onChange(nextFields);
  };

  const updateShowPayBy = (nextShowPayBy: IndeedShowPayBy | undefined) => {
    const nextFields: IndeedFields = { ...value, showPayBy: nextShowPayBy };

    if (indeedPayUsesMinimumAndMaximum(nextShowPayBy)) {
      nextFields.amount = undefined;
    } else {
      nextFields.minimum = undefined;
      nextFields.maximum = undefined;
    }

    onChange(nextFields);
  };

  const updatePayTransparencyExempt = (checked: boolean) => {
    onChange({ ...value, payTransparencyExempt: checked });
  };

  const fields = (
    <>
      {infoAlert}

      {renderField({
        id: 'job_form_indeed_location_type',
        label: 'Job location type',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.jobLocationType || ''}
            onChange={e => updateLocationType((e.target.value || undefined) as IndeedFields['jobLocationType'])}
          >
            <MenuItem value="">None</MenuItem>
            {indeedJobLocationTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {showRemoteResidenceQuestion
        ? renderField({
            id: 'job_form_indeed_remote_residence',
            label: 'Are employees required to reside in a specific location?',
            flexOptions,
            renderControl: () => (
              <TextField
                select
                fullWidth
                size="small"
                value={
                  value.requireRemoteResidence === true ? 'yes' : value.requireRemoteResidence === false ? 'no' : ''
                }
                onChange={e =>
                  updateRequireRemoteResidence(
                    e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined,
                  )
                }
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            ),
          })
        : null}

      {showJobLocation
        ? renderField({
            id: 'job_form_indeed_job_location',
            label: jobLocationLabel,
            flexOptions,
            renderControl: () => (
              <ExtensionLocationAutocomplete
                value={value.jobLocation || ''}
                onChange={next => updateField('jobLocation', next || undefined)}
                extensionInstalled={extensionInstalled}
                platformLabel="Indeed"
                placeholder="Search Indeed job location"
                helperText="Select Location from Indeed suggestions"
                fetchSuggestions={fetchIndeedLocationSuggestions}
              />
            ),
          })
        : null}

      {showWillingToRelocate
        ? renderField({
            id: 'job_form_indeed_relocate',
            label: 'Open to relocation',
            flexOptions,
            renderControl: () => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.includeWillingToRelocate ?? false}
                    onChange={e => updateField('includeWillingToRelocate', e.target.checked)}
                  />
                }
                label="I'm interested in attracting candidates open to relocation"
              />
            ),
          })
        : null}

      {renderField({
        id: 'job_form_indeed_hiring_timeline',
        label: 'Hiring timeline for this job',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.hiringTimeline || ''}
            onChange={e =>
              updateField('hiringTimeline', (e.target.value || undefined) as IndeedFields['hiringTimeline'])
            }
          >
            <MenuItem value="">None</MenuItem>
            {indeedHiringTimelineOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_indeed_hires_needed',
        label: 'Number of people to hire in the next 30 days',
        flexOptions,
        renderControl: () => (
          <TextField
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 1, step: 1 }}
            value={value.hiresNeeded ?? ''}
            onChange={e => {
              const parsed = Number.parseInt(e.target.value, 10);
              updateField('hiresNeeded', Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
            }}
            placeholder="1"
          />
        ),
      })}

      {renderField({
        id: 'job_form_indeed_job_types',
        label: 'Job type',
        flexOptions,
        renderControl: () => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {indeedJobTypeOptions.map(option => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={value.jobTypes?.includes(option.value) ?? false}
                    onChange={e => toggleJobType(option.value, e.target.checked)}
                  />
                }
                label={option.label}
              />
            ))}
          </Box>
        ),
      })}

      {showExpectedHours
        ? renderField({
            id: 'job_form_indeed_expected_hours_show_by',
            label: 'Expected hours',
            flexOptions,
            renderControl: () => (
              <TextField
                select
                fullWidth
                size="small"
                value={expectedHoursShowBy}
                onChange={e =>
                  updateField(
                    'expectedHoursShowBy',
                    (e.target.value || undefined) as IndeedFields['expectedHoursShowBy'],
                  )
                }
              >
                {indeedExpectedHoursShowByOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            ),
          })
        : null}

      {showExpectedHours && expectedHoursShowBy === 'Fixed hours'
        ? renderField({
            id: 'job_form_indeed_expected_hours_at',
            label: 'Fixed at (hours per week)',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={value.expectedHoursAt ?? ''}
                onChange={e => {
                  const parsed = Number.parseInt(e.target.value, 10);
                  updateField('expectedHoursAt', Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
                }}
              />
            ),
          })
        : null}

      {showExpectedHours && expectedHoursShowBy === 'Range'
        ? renderField({
            id: 'job_form_indeed_expected_hours_range',
            label: 'Hours per week range',
            flexOptions,
            renderControl: () => (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="From"
                  inputProps={{ min: 1, step: 1 }}
                  value={value.expectedHoursFrom ?? ''}
                  onChange={e => {
                    const parsed = Number.parseInt(e.target.value, 10);
                    updateField('expectedHoursFrom', Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="To"
                  inputProps={{ min: 1, step: 1 }}
                  value={value.expectedHoursTo ?? ''}
                  onChange={e => {
                    const parsed = Number.parseInt(e.target.value, 10);
                    updateField('expectedHoursTo', Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
                  }}
                />
              </Box>
            ),
          })
        : null}

      {showExpectedHours && expectedHoursShowBy === 'Maximum'
        ? renderField({
            id: 'job_form_indeed_expected_hours_maximum',
            label: 'No more than (hours per week)',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={value.expectedHoursMaximum ?? ''}
                onChange={e => {
                  const parsed = Number.parseInt(e.target.value, 10);
                  updateField('expectedHoursMaximum', Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
                }}
              />
            ),
          })
        : null}

      {showExpectedHours && expectedHoursShowBy === 'Minimum'
        ? renderField({
            id: 'job_form_indeed_expected_hours_minimum',
            label: 'No less than (hours per week)',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={value.expectedHoursMinimum ?? ''}
                onChange={e => {
                  const parsed = Number.parseInt(e.target.value, 10);
                  updateField('expectedHoursMinimum', Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
                }}
              />
            ),
          })
        : null}

      {renderField({
        id: 'job_form_indeed_show_pay_by',
        label: 'Show pay by',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={showPayBy}
            disabled={value.payTransparencyExempt === true}
            onChange={e => updateShowPayBy((e.target.value || undefined) as IndeedShowPayBy | undefined)}
          >
            {indeedShowPayByOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {indeedPayUsesMinimumAndMaximum(showPayBy)
        ? renderField({
            id: 'job_form_indeed_minimum',
            label: 'Minimum',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                value={payFieldDisplay(value.minimum)}
                disabled={value.payTransparencyExempt === true}
                onChange={e => updateField('minimum', parsePayAmount(e.target.value))}
              />
            ),
          })
        : null}

      {indeedPayUsesMinimumAndMaximum(showPayBy)
        ? renderField({
            id: 'job_form_indeed_maximum',
            label: 'Maximum',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                value={payFieldDisplay(value.maximum)}
                disabled={value.payTransparencyExempt === true}
                onChange={e => updateField('maximum', parsePayAmount(e.target.value))}
              />
            ),
          })
        : null}

      {indeedPayUsesAmount(showPayBy)
        ? renderField({
            id: 'job_form_indeed_amount',
            label: 'Amount',
            flexOptions,
            renderControl: () => (
              <TextField
                fullWidth
                size="small"
                value={payFieldDisplay(value.amount)}
                disabled={value.payTransparencyExempt === true}
                onChange={e => updateField('amount', parsePayAmount(e.target.value))}
              />
            ),
          })
        : null}

      {renderField({
        id: 'job_form_indeed_rate',
        label: 'Rate',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.rate || 'per year'}
            disabled={value.payTransparencyExempt === true}
            onChange={e => updateField('rate', (e.target.value || undefined) as IndeedFields['rate'])}
          >
            {indeedPayRateOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_indeed_pay_transparency_exempt',
        label: 'Pay transparency',
        flexOptions,
        renderControl: () => (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.payTransparencyExempt ?? false}
                onChange={e => updatePayTransparencyExempt(e.target.checked)}
              />
            }
            label="This job is exempt from local laws that require pay ranges. Other pay information will be included in the job description."
          />
        ),
      })}

      {renderField({
        id: 'job_form_indeed_benefits',
        label: 'Benefits',
        flexOptions,
        renderControl: () => (
          <Box>
            <Autocomplete
              multiple
              options={getIndeedBenefitPickerOptions(selectedBenefits)}
              value={selectedBenefits}
              onChange={(_, next) => updateField('benefits', next.length > 0 ? next : [])}
              filterSelectedOptions
              disableCloseOnSelect
              getOptionLabel={option => option}
              isOptionEqualToValue={(option, optionValue) => option === optionValue}
              renderInput={params => <TextField {...params} size="small" placeholder="Search Indeed benefits" />}
            />
            <FormHelperText>
              Indeed only shows some of these for each job. Autofill checks the ones that appear.
            </FormHelperText>
          </Box>
        ),
      })}
    </>
  );

  if (!wrapInGroup) {
    return fields;
  }

  return (
    <Group
      title={hideGroupTitle ? undefined : 'Job Autofill for Indeed (optional)'}
      bg="white"
      id="job_form_indeed_fields"
      isCollapsible={!hideGroupTitle}
      defaultExpanded={false}
    >
      {fields}
    </Group>
  );
};

export default IndeedFieldsForm;
